import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";
import { api, apiError, fileUrl } from "../lib/api";
import { fmtDateStr } from "../lib/format";

const money = (n, cur) => new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD" }).format(Number(n || 0));
const to12 = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; const hh = h % 12 || 12; return `${hh}:${String(m).padStart(2, "0")} ${ap}`; };

export default function ApprovePage() {
  const { token } = useParams();
  const preAction = new URLSearchParams(window.location.search).get("action");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [submitErr, setSubmitErr] = useState(null);

  useEffect(() => {
    api.get(`/public/approvals/${token}`)
      .then(({ data }) => { setData(data); if (data.status !== "pending") setResult(data.status); })
      .catch((e) => setErr(apiError(e.response?.data?.detail) || "This approval link is invalid."));
  }, [token]);

  const decide = async (decision) => {
    setBusy(true); setSubmitErr(null);
    try {
      const { data: res } = await api.post(`/public/approvals/${token}/decide`, { decision, note });
      setResult(res.status);
    } catch (e) { setSubmitErr(apiError(e.response?.data?.detail) || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  if (err) return <Shell><div className="text-center py-10" data-testid="approve-error"><h1 className="text-2xl font-bold mb-2">Unavailable</h1><p className="text-slate-400">{err}</p></div></Shell>;
  if (!data) return <Shell><div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div></Shell>;

  const accent = data.company?.primary_color || "#1d54c4";
  const logo = data.company?.logo_path ? fileUrl(data.company.logo_path) : "";
  const cur = data.company?.currency || "USD";

  if (result) {
    const approved = result === "approved";
    return (
      <Shell accent={accent} logo={logo} name={data.company?.name}>
        <div className="text-center py-10" data-testid="approve-result">
          {approved ? <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400" /> : <XCircle className="w-16 h-16 mx-auto text-rose-400" />}
          <h1 className="text-2xl font-bold mt-4">{approved ? "Approved" : "Declined"}</h1>
          <p className="text-slate-400 mt-2">{`${data.type === "payroll" ? "Payroll" : "Schedule"} approval — ${fmtDateStr(data.period_start)} to ${fmtDateStr(data.period_end)}`}</p>
          <p className="text-slate-500 text-sm mt-4">The requester has been notified. You can close this page.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell accent={accent} logo={logo} name={data.company?.name}>
      <div data-testid="approve-page">
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-full px-3 py-1 mb-3">
          <ShieldCheck className="w-3.5 h-3.5" /> Approval request
        </div>
        <h1 className="text-2xl font-bold">{`${data.type === "payroll" ? "Payroll" : "Schedule"} approval — ${fmtDateStr(data.period_start)} to ${fmtDateStr(data.period_end)}`}</h1>
        <p className="text-slate-400 text-sm mt-1">Submitted by {data.requested_by_name} · {fmtDateStr(data.period_start)} to {fmtDateStr(data.period_end)}</p>

        <div className="mt-6 rounded-2xl border border-slate-800 overflow-hidden">
          {data.type === "payroll" ? (
            <table className="w-full text-sm" data-testid="approve-payroll-table">
              <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
                <tr><th className="text-left px-4 py-2.5">Employee</th><th className="text-right px-4 py-2.5">Reg</th><th className="text-right px-4 py-2.5">OT</th><th className="text-right px-4 py-2.5">Total</th><th className="text-right px-4 py-2.5">Gross</th></tr>
              </thead>
              <tbody>
                {(data.snapshot?.rows || []).map((r) => (
                  <tr key={r.employee_id} className="border-t border-slate-800">
                    <td className="px-4 py-2.5 font-medium">{r.employee_name}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.regular_hours}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.overtime_hours}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.total_hours}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{money(r.gross_pay, cur)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-700 font-semibold bg-slate-950/40">
                  <td className="px-4 py-2.5">Total</td><td /><td /><td className="px-4 py-2.5 text-right font-mono">{data.snapshot?.total_hours}</td><td className="px-4 py-2.5 text-right font-mono">{money(data.snapshot?.total_gross, cur)}</td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="approve-schedule-table">
              <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
                <tr><th className="text-left px-4 py-2.5">Employee</th><th className="text-left px-4 py-2.5">Date</th><th className="text-left px-4 py-2.5">Shift</th><th className="text-left px-4 py-2.5">Note</th></tr>
              </thead>
              <tbody>
                {(data.snapshot?.shifts || []).map((s, i) => (
                  <tr key={i} className="border-t border-slate-800">
                    <td className="px-4 py-2.5 font-medium">{s.employee_name}</td>
                    <td className="px-4 py-2.5">{fmtDateStr(s.date)}</td>
                    <td className="px-4 py-2.5 font-mono">{to12(s.start)}–{to12(s.end)}</td>
                    <td className="px-4 py-2.5 text-slate-400">{s.note || "—"}</td>
                  </tr>
                ))}
                {(!data.snapshot?.shifts || data.snapshot.shifts.length === 0) && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">No shifts in this range.</td></tr>}
              </tbody>
            </table>
          )}
        </div>

        <textarea data-testid="approve-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add an optional note…" className="mt-5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none resize-none" />

        {submitErr && <div data-testid="approve-submit-error" className="mt-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 px-4 py-2.5 text-sm">{submitErr}</div>}

        <div className="grid grid-cols-2 gap-3 mt-4">
          <button data-testid="approve-decline-button" disabled={busy} onClick={() => decide("declined")} className="rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-60 py-3 font-semibold inline-flex items-center justify-center gap-2 transition-colors"><XCircle className="w-5 h-5" /> Decline</button>
          <button data-testid="approve-approve-button" disabled={busy} onClick={() => decide("approved")} className="rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 py-3 font-semibold inline-flex items-center justify-center gap-2 transition-colors"><CheckCircle2 className="w-5 h-5" /> Approve</button>
        </div>
        {preAction && <p className="text-center text-xs text-slate-500 mt-3">Review the details above, then confirm your <b>{preAction}</b> decision.</p>}
      </div>
    </Shell>
  );
}

function Shell({ children, accent = "#1d54c4", logo, name }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="h-2" style={{ backgroundColor: accent }} />
      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center gap-3 mb-8">
          {logo ? <img src={logo} alt="logo" className="h-10 w-auto bg-white rounded-lg p-1" /> : null}
          <span className="font-bold text-lg text-center">{name || "RightPay"}</span>
        </div>
        {children}
        <p className="text-center text-xs text-slate-600 mt-10">Powered by RightPay</p>
      </div>
    </div>
  );
}
